import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Alert } from "@/components/ui/Alert";
import { Card } from "@/components/ui/Card";
import { SelectInput } from "@/components/ui/Field";
import { Spinner } from "@/components/ui/Spinner";
import { ApiError } from "@/lib/apiClient";
import {
  activateHeroDokument,
  RECHNUNG_ART_LABELS,
  ZAHLUNGSSTATUS_LABELS,
  type RechnungArt,
} from "@/lib/dokumente";
import {
  formatMahnungDate,
  getKundenKontoByGp,
  getKundenRechnungenByGp,
  MAHNSTUFE_LABELS,
  mahnungStatusLabel,
  type KundenKonto,
  type KundenRechnung,
} from "@/lib/mahnwesen";
import { toast } from "@/lib/toast";
import { useDocumentEditor } from "@/pages/dokumente/useDocumentEditor";
import {
  distinctObjektadressen,
  filterKundenRechnungen,
  sortKundenRechnungen,
  type KundenRechnungSort,
} from "@/pages/kontakte/kundenRechnungen";

function euro(value: number): string {
  return value.toLocaleString("de-DE", { style: "currency", currency: "EUR" });
}

interface KundeMahnwesenSectionProps {
  geschaeftspartnerId: number;
}

export function KundeMahnwesenSection({ geschaeftspartnerId }: KundeMahnwesenSectionProps) {
  const { open: openDocumentEditor } = useDocumentEditor();
  const [konto, setKonto] = useState<(KundenKonto & { kunden_id: number }) | null>(null);
  const [nichtVerknuepft, setNichtVerknuepft] = useState(false);
  const [rechnungen, setRechnungen] = useState<KundenRechnung[]>([]);
  const [kundennummer, setKundennummer] = useState<string | null>(null);
  const [truncated, setTruncated] = useState(false);
  const [sort, setSort] = useState<KundenRechnungSort>("datum");
  const [objektadresse, setObjektadresse] = useState("");
  const [openingId, setOpeningId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [rechnungError, setRechnungError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setRechnungError(null);
    setObjektadresse("");
    const [kontoResult, rechnungResult] = await Promise.allSettled([
      getKundenKontoByGp(geschaeftspartnerId),
      getKundenRechnungenByGp(geschaeftspartnerId),
    ]);
    if (kontoResult.status === "fulfilled") {
      const data = kontoResult.value;
      if (data.kunden_id == null) {
        setNichtVerknuepft(true);
        setKonto(null);
      } else {
        setNichtVerknuepft(false);
        setKonto(data);
      }
    } else {
      const err = kontoResult.reason;
      setError(err instanceof ApiError ? err.message : "Forderungskonto konnte nicht geladen werden.");
      setKonto(null);
    }
    if (rechnungResult.status === "fulfilled") {
      setRechnungen(rechnungResult.value.rechnungen || []);
      setKundennummer(rechnungResult.value.kundennummer);
      setTruncated(Boolean(rechnungResult.value.truncated));
    } else {
      const err = rechnungResult.reason;
      setRechnungError(
        err instanceof ApiError ? err.message : "Rechnungen konnten nicht geladen werden.",
      );
      setRechnungen([]);
    }
    setLoading(false);
  }, [geschaeftspartnerId]);

  useEffect(() => {
    void load();
  }, [load]);

  const adressen = useMemo(() => distinctObjektadressen(rechnungen), [rechnungen]);
  const sichtbareRechnungen = useMemo(
    () => sortKundenRechnungen(filterKundenRechnungen(rechnungen, objektadresse), sort),
    [objektadresse, rechnungen, sort],
  );

  const openRechnung = useCallback(
    async (row: KundenRechnung) => {
      if (openingId) return;
      if (row.dokument_id) {
        openDocumentEditor({ docId: row.dokument_id });
        return;
      }
      const heroId = row.hero_document_id;
      if (!heroId || heroId <= 0) {
        toast.error("Rechnung ohne Dokument-ID.");
        return;
      }
      setOpeningId(row.id);
      try {
        const activated = await activateHeroDokument(heroId);
        openDocumentEditor({ docId: activated.id });
      } catch (err) {
        toast.error(
          err instanceof ApiError ? err.message : "Hero-Rechnung konnte nicht geöffnet werden.",
        );
      } finally {
        setOpeningId(null);
      }
    },
    [openDocumentEditor, openingId],
  );

  const kpis = useMemo(() => {
    if (!konto) return null;
    const offen = konto.offene_posten.reduce((sum, op) => sum + op.summe_offen, 0);
    const ueberfaellig = konto.offene_posten
      .filter((op) => op.tage_ueberfaellig > 0)
      .reduce((sum, op) => sum + op.summe_offen, 0);
    const aktiveSperren = konto.sperren.filter((s) => s.aktiv).length;
    const aktiveVereinbarungen = konto.vereinbarungen.filter(
      (v) => v.status === "aktiv",
    ).length;
    return { offen, ueberfaellig, aktiveSperren, aktiveVereinbarungen };
  }, [konto]);

  if (loading) {
    return (
      <Card title="Forderungen & Mahnwesen">
        <div className="flex items-center gap-2 text-sm text-label-secondary">
          <Spinner /> Laden …
        </div>
      </Card>
    );
  }

  const rechnungsTitel = kundennummer
    ? `Rechnungen zu Kundennummer ${kundennummer}`
    : "Rechnungen dieses Kunden";

  return (
    <div className="space-y-4">
      <Card
        title={rechnungsTitel}
        description="Alle Rechnungen zu dieser Kundennummer. Bei mehreren Objektadressen lässt sich die Liste danach sortieren und eingrenzen."
      >
        {rechnungError ? <Alert variant="error">{rechnungError}</Alert> : null}
        {!rechnungError && rechnungen.length === 0 ? (
          <p className="text-sm text-label-secondary">
            Für diese Kundennummer liegen keine Rechnungen vor.
          </p>
        ) : null}
        {!rechnungError && rechnungen.length > 0 ? (
          <div className="space-y-3">
            <div className="flex flex-wrap items-end gap-3">
              <SelectInput
                label="Sortierung"
                selectSize="sm"
                value={sort}
                onChange={(event) => setSort(event.target.value as KundenRechnungSort)}
              >
                <option value="datum">Belegdatum</option>
                <option value="objektadresse">Objektadresse</option>
                <option value="nummer">Rechnungsnummer</option>
              </SelectInput>
              {adressen.length > 1 ? (
                <SelectInput
                  label="Objektadresse"
                  selectSize="sm"
                  value={objektadresse}
                  onChange={(event) => setObjektadresse(event.target.value)}
                >
                  <option value="">Alle Objektadressen</option>
                  {adressen.map((adresse) => (
                    <option key={adresse} value={adresse}>
                      {adresse}
                    </option>
                  ))}
                </SelectInput>
              ) : null}
            </div>
            {truncated ? (
              <p className="text-xs text-label-secondary">
                Es werden die 500 neuesten Rechnungen angezeigt.
              </p>
            ) : null}
            <div className="overflow-x-auto">
              <table className="min-w-full text-left text-sm">
                <thead className="text-xs uppercase tracking-wide text-label-secondary">
                  <tr>
                    <th className="py-1.5 pr-4 font-semibold">Rechnung</th>
                    <th className="py-1.5 pr-4 font-semibold">Datum</th>
                    <th className="py-1.5 pr-4 font-semibold">Objektadresse</th>
                    <th className="py-1.5 pr-4 text-right font-semibold">Brutto</th>
                    <th className="py-1.5 pr-4 text-right font-semibold">Offen</th>
                    <th className="py-1.5 font-semibold">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {sichtbareRechnungen.map((row) => {
                    const art = row.rechnung_art
                      ? RECHNUNG_ART_LABELS[row.rechnung_art as RechnungArt]
                      : null;
                    return (
                      <tr key={row.id} className="border-t border-separator/60">
                        <td className="py-1.5 pr-4">
                          <button
                            type="button"
                            className="font-medium text-accent hover:underline disabled:opacity-60"
                            disabled={openingId != null}
                            onClick={() => void openRechnung(row)}
                          >
                            {openingId === row.id
                              ? "Öffnen …"
                              : row.dokument_nummer || `#${row.dokument_id || row.hero_document_id}`}
                          </button>
                          {art ? (
                            <div className="text-xs text-label-secondary">{art}</div>
                          ) : null}
                        </td>
                        <td className="py-1.5 pr-4 tabular-nums">
                          {formatMahnungDate(row.belegdatum)}
                        </td>
                        <td className="py-1.5 pr-4">
                          {row.objektadresse || "—"}
                          {row.auftragsnummer ? (
                            <div className="text-xs text-label-secondary">{row.auftragsnummer}</div>
                          ) : null}
                        </td>
                        <td className="py-1.5 pr-4 text-right tabular-nums">
                          {euro(row.summe_brutto)}
                        </td>
                        <td className="py-1.5 pr-4 text-right tabular-nums">
                          {euro(row.summe_offen)}
                        </td>
                        <td className="py-1.5">
                          <span
                            className={
                              row.zahlungsstatus === "bezahlt"
                                ? "text-status-green"
                                : row.zahlungsstatus === "teilbezahlt"
                                  ? "text-status-orange"
                                  : undefined
                            }
                          >
                            {ZAHLUNGSSTATUS_LABELS[row.zahlungsstatus]}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {sichtbareRechnungen.length === 0 ? (
              <p className="text-sm text-label-secondary">
                Keine Rechnungen für die gewählte Objektadresse.
              </p>
            ) : null}
          </div>
        ) : null}
      </Card>

      {error ? (
        <Card title="Forderungen & Mahnwesen">
          <Alert variant="error">{error}</Alert>
        </Card>
      ) : null}

      {!error && (nichtVerknuepft || !konto) ? (
        <Card title="Forderungen & Mahnwesen">
          <p className="text-sm text-label-secondary">
            Dieser Geschäftspartner ist keinem Abrechnungs-Kunden zugeordnet. Offene Posten und
            Mahnungen erscheinen, sobald diese Zuordnung besteht.
          </p>
        </Card>
      ) : null}

      {konto ? (
        <div className="space-y-4">
      {kpis ? (
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <KpiTile label="Offen gesamt" value={euro(kpis.offen)} />
          <KpiTile
            label="Davon überfällig"
            value={euro(kpis.ueberfaellig)}
            tone={kpis.ueberfaellig > 0 ? "warn" : undefined}
          />
          <KpiTile
            label="Aktive Mahnsperren"
            value={String(kpis.aktiveSperren)}
            tone={kpis.aktiveSperren > 0 ? "warn" : undefined}
          />
          <KpiTile label="Zahlungsvereinbarungen" value={String(kpis.aktiveVereinbarungen)} />
        </div>
      ) : null}

      <Card
        title="Offene Posten"
        description="Unbezahlte Rechnungen dieses Kunden mit aktuellem Mahnstatus."
        actions={
          <Link to="/dokumente/mahnwesen" className="text-sm font-medium text-accent hover:underline">
            Zum Mahnwesen
          </Link>
        }
      >
        {konto.offene_posten.length === 0 ? (
          <p className="text-sm text-label-secondary">Keine offenen Rechnungen.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead className="text-xs uppercase tracking-wide text-label-secondary">
                <tr>
                  <th className="py-1.5 pr-4 font-semibold">Rechnung</th>
                  <th className="py-1.5 pr-4 font-semibold">Fällig</th>
                  <th className="py-1.5 pr-4 font-semibold">Überfällig</th>
                  <th className="py-1.5 pr-4 text-right font-semibold">Offen</th>
                  <th className="py-1.5 font-semibold">Mahnstatus</th>
                </tr>
              </thead>
              <tbody>
                {konto.offene_posten.map((op) => (
                  <tr key={op.id} className="border-t border-separator/60">
                    <td className="py-1.5 pr-4">
                      <Link
                        to={`/dokumente/${op.id}`}
                        className="font-medium text-accent hover:underline"
                      >
                        {op.dokument_nummer || `#${op.id}`}
                      </Link>
                    </td>
                    <td className="py-1.5 pr-4 tabular-nums">
                      {formatMahnungDate(op.faelligkeitsdatum)}
                    </td>
                    <td className="py-1.5 pr-4">
                      {op.tage_ueberfaellig > 0 ? (
                        <span className="font-medium text-status-orange">
                          {op.tage_ueberfaellig} Tage
                        </span>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className="py-1.5 pr-4 text-right tabular-nums">{euro(op.summe_offen)}</td>
                    <td className="py-1.5 text-xs">
                      {MAHNSTUFE_LABELS[op.mahnstufe] || op.mahnstufe || "Keine"}
                      {op.mahnung_pausiert ? (
                        <span className="ml-1.5 rounded-full bg-status-orange/15 px-1.5 py-0.5 text-[11px] font-medium text-status-orange">
                          Pausiert
                        </span>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card title="Mahnungen" description="Alle Mahnungen und Zahlungserinnerungen dieses Kunden.">
        {konto.mahnungen.length === 0 ? (
          <p className="text-sm text-label-secondary">Bisher keine Mahnungen.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead className="text-xs uppercase tracking-wide text-label-secondary">
                <tr>
                  <th className="py-1.5 pr-4 font-semibold">Nummer</th>
                  <th className="py-1.5 pr-4 font-semibold">Stufe</th>
                  <th className="py-1.5 pr-4 font-semibold">Status</th>
                  <th className="py-1.5 pr-4 font-semibold">Datum</th>
                  <th className="py-1.5 pr-4 text-right font-semibold">Gesamt</th>
                  <th className="py-1.5 font-semibold">Rechnungen</th>
                </tr>
              </thead>
              <tbody>
                {konto.mahnungen.map((m) => (
                  <tr key={m.id} className="border-t border-separator/60">
                    <td className="py-1.5 pr-4">
                      {m.legacy ? (
                        <span className="text-label-secondary">{m.nummer || `#${m.id}`}</span>
                      ) : (
                        <Link
                          to={`/dokumente/mahnwesen/${m.id}`}
                          className="font-medium text-accent hover:underline"
                        >
                          {m.nummer || `Entwurf #${m.id}`}
                        </Link>
                      )}
                    </td>
                    <td className="py-1.5 pr-4">{m.stufe_bezeichnung}</td>
                    <td className="py-1.5 pr-4">
                      {mahnungStatusLabel(m.status, true)}
                    </td>
                    <td className="py-1.5 pr-4 tabular-nums">
                      {formatMahnungDate(m.versendet_am || m.belegdatum || m.created_at)}
                    </td>
                    <td className="py-1.5 pr-4 text-right tabular-nums">{euro(m.summe_gesamt)}</td>
                    <td className="py-1.5 text-xs text-label-secondary">
                      {m.rechnungsnummern || "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {konto.sperren.length > 0 || konto.vereinbarungen.length > 0 ? (
        <Card title="Sperren & Vereinbarungen">
          <div className="space-y-3">
            {konto.sperren.map((s) => (
              <div
                key={`sperre-${s.id}`}
                className="flex flex-wrap items-center gap-2 rounded-lg border border-separator/70 px-3 py-2 text-sm"
              >
                <span
                  className={
                    s.aktiv
                      ? "rounded-full bg-status-red/15 px-2 py-0.5 text-[11px] font-medium text-status-red"
                      : "rounded-full bg-fill-tertiary px-2 py-0.5 text-[11px] text-label-secondary"
                  }
                >
                  {s.aktiv ? "Aktive Sperre" : "Beendete Sperre"}
                </span>
                <span className="text-label">{s.grund || s.status}</span>
                {s.gueltig_bis ? (
                  <span className="text-xs text-label-secondary">
                    bis {formatMahnungDate(s.gueltig_bis)}
                  </span>
                ) : null}
              </div>
            ))}
            {konto.vereinbarungen.map((v) => (
              <div
                key={`vereinb-${v.id}`}
                className="flex flex-wrap items-center gap-2 rounded-lg border border-separator/70 px-3 py-2 text-sm"
              >
                <span className="rounded-full bg-fill-tertiary px-2 py-0.5 text-[11px] text-label-secondary">
                  Vereinbarung ({v.status})
                </span>
                <span className="text-label">{v.titel || "Zahlungsvereinbarung"}</span>
                {v.naechste_rate_am ? (
                  <span className="text-xs text-label-secondary">
                    nächste Rate {formatMahnungDate(v.naechste_rate_am)}
                    {v.naechste_rate_betrag != null ? ` · ${euro(v.naechste_rate_betrag)}` : ""}
                  </span>
                ) : null}
              </div>
            ))}
          </div>
        </Card>
      ) : null}
        </div>
      ) : null}
    </div>
  );
}

function KpiTile({ label, value, tone }: { label: string; value: string; tone?: "warn" }) {
  return (
    <div className="rounded-ios border border-separator bg-card p-3 shadow-ios">
      <div className="text-xs font-medium text-label-secondary">{label}</div>
      <div
        className={
          tone === "warn"
            ? "mt-0.5 text-lg font-semibold tabular-nums text-status-orange"
            : "mt-0.5 text-lg font-semibold tabular-nums text-label"
        }
      >
        {value}
      </div>
    </div>
  );
}
